// ============================================================
// useCurtain - state for a curtain overlay docked to a panel edge.
//
// Generalised from the combat log's overlay (v8.242.0): the log
// hangs over the tactical map, the actor status curtain hangs over
// the attacker panel, and the machinery is identical - a persisted
// height percentage, a persisted collapsed flag, a drag-to-resize
// grip with pointer capture, and the YIELD borrow channel ("somebody
// else needs this space for now") that never overwrites the user's
// stored preference. Pref keys and the container testid are the
// parameters; useLogOverlay wraps this with its original keys so
// nobody's saved layout moves. The curtain may hang from either edge
// (`dock`), and its borrow may be made ahead of it (`useCurtainBorrow`).
// ============================================================
import { useState, useRef, useCallback, useSyncExternalStore } from 'react';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../utils/viewerPrefs';

function usePrefs() {
  return useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
}

/**
 * @param {object} opts
 * @param {string} opts.heightKey     viewerPrefs key for the height %
 * @param {string} opts.collapsedKey  viewerPrefs key for the collapsed flag
 * @param {string} opts.containerTestId  data-testid of the curtain root -
 *   the resize grip finds its geometry through `closest()` on it
 * @param {number} [opts.defaultHeight=55]
 * @param {number} [opts.minHeight=12]
 * @param {number} [opts.maxHeight=92]
 * @param {boolean} [opts.defaultCollapsed=false]  the log defaults open,
 *   the actor curtain defaults collapsed - an UNSET pref reads as this
 * @param {'top'|'bottom'} [opts.dock='top']  which edge of the panel the
 *   curtain hangs from; the grip's maths follows (plan design-gaps, L3)
 * @param {object} opts.borrow  the curtain's borrow, made by the caller
 *   with `useCurtainBorrow` - earlier than the keys when its yield must
 *   exist first (the log's: the shell's sync yields it before ShellMain
 *   makes the curtain), beside them otherwise (the handouts drawer)
 */
export default function useCurtain(opts) {
  const { heightKey, collapsedKey, containerTestId, defaultHeight = 55, minHeight = 12, maxHeight = 92 } = opts;
  const { defaultCollapsed = false, dock = 'top' } = opts;
  const prefs = usePrefs();
  const clamp = useCallback((n) => Math.min(maxHeight, Math.max(minHeight, n)), [minHeight, maxHeight]);
  const height = typeof prefs[heightKey] === 'number' ? clamp(prefs[heightKey]) : defaultHeight;
  const stored = storedCollapsed(prefs, collapsedKey, defaultCollapsed);
  const setHeight = useCallback((h) => setViewerPref(heightKey, h), [heightKey]);
  const setCollapsed = useCollapsedSetter(collapsedKey, defaultCollapsed);
  const { borrow } = opts;
  const toggleCollapsed = borrow.toggleWith(setCollapsed);
  const resizeHandlers = useResizeGrip({ containerTestId, collapsed: stored, setCollapsed, setHeight, clamp, dock });
  return {
    height,
    setHeight,
    // WHAT IS ON SCREEN is the user's choice OR the borrow; what is
    // written down is only ever the choice.
    collapsed: (stored && !borrow.summoned) || borrow.yielded,
    setCollapsed,
    toggleCollapsed,
    setYield: borrow.setYield,
    summon: borrow.summon,
    releaseSummon: borrow.releaseSummon,
    summoned: borrow.summoned,
    resizeHandlers,
  };
}

/** The stored collapsed flag - an unset (or non-boolean) pref reads as the default. */
function storedCollapsed(prefs, key, fallback) {
  return typeof prefs[key] === 'boolean' ? prefs[key] : fallback;
}

/** Write the collapsed pref; a function is resolved against what is stored NOW (or the default). */
function useCollapsedSetter(collapsedKey, defaultCollapsed) {
  return useCallback(
    (next) => {
      const current = () => storedCollapsed(getViewerPrefs(), collapsedKey, defaultCollapsed);
      setViewerPref(collapsedKey, typeof next === 'function' ? next(current()) : next);
    },
    [collapsedKey, defaultCollapsed],
  );
}

/**
 * SOMEBODY ELSE HAS THE SPACE FOR NOW - transient, never persisted.
 *
 * The borrow used to be done by writing `collapsed` and remembering the old
 * value in a ref, which failed four ways (v8.36.0 - stranded refs, slammed
 * manual expands, missing exits, lost remounts), and in every one the
 * user's OWN choice had already been overwritten in localStorage. Keeping
 * the borrow separate makes all four harmless: the stored value is only
 * ever what the user asked for.
 *
 * SUMMONED (v8.363.5) is the mirror of yield - opened for a moment without
 * touching the stored preference (a private vox for you). Released by the
 * next toggle, which then writes what the user actually chose.
 */
export function useCurtainBorrow() {
  const [yielded, setYielded] = useState(false);
  /** Asked for the curtain back during THIS yield - do not take it again. */
  const released = useRef(false);
  const [summoned, setSummoned] = useState(false);
  const summon = useCallback(() => setSummoned(true), []);
  const releaseSummon = useCallback(() => setSummoned(false), []);
  /** Idempotent, so the caller states the current truth on every render. */
  const setYield = useCallback((on) => {
    if (!on) {
      released.current = false;
      setYielded(false);
      return;
    }
    if (!released.current) setYielded(true);
  }, []);
  const toggleWith = useToggleWith({ yielded, setYielded, released, summoned, setSummoned });
  return { yielded, setYield, summoned, summon, releaseSummon, toggleWith };
}

/**
 * The user's toggle, made for a given collapsed-setter (the keys may come
 * later than the borrow). A summon is released by closing (and writing
 * that); EXPANDING DURING A YIELD WINS, and keeps winning until the mode
 * ends - it also writes the preference, which is what makes the choice
 * survive the way out.
 */
function useToggleWith({ yielded, setYielded, released, summoned, setSummoned }) {
  return useCallback(
    (setCollapsed) => () => {
      if (summoned) {
        setSummoned(false);
        setCollapsed(true);
        return;
      }
      if (yielded) {
        released.current = true;
        setYielded(false);
        setCollapsed(false);
        return;
      }
      setCollapsed((c) => !c);
    },
    [yielded, setYielded, released, summoned, setSummoned],
  );
}

/** Capture the pointer and measure the panel once; null for a grip outside its curtain. */
function beginResize(e, containerTestId) {
  const container = e.currentTarget.closest(`[data-testid="${containerTestId}"]`)?.parentElement;
  if (!container) return null;
  e.preventDefault();
  e.currentTarget.setPointerCapture?.(e.pointerId);
  const { top, bottom } = container.getBoundingClientRect();
  return { id: e.pointerId, top, bottom, height: container.clientHeight };
}

/** How far into the panel the pointer is, from the edge the curtain hangs from. */
function draggedPct(d, clientY, dock) {
  const from = dock === 'bottom' ? d.bottom - clientY : clientY - d.top;
  return (from / d.height) * 100;
}

/**
 * Drag the edge to resize. Pointer capture, not a container mousemove: the
 * natural gesture runs past the panel's edge. The panel element is
 * measured once at the start, so the percentage stays correct without
 * reading layout on every move.
 *
 * One bag to spread onto the grip: the three handlers are meaningless
 * apart, and pointer capture only works if they all sit on the element
 * that captured.
 */
function useResizeGrip({ containerTestId, collapsed, setCollapsed, setHeight, clamp, dock }) {
  const drag = useRef(null);
  const onPointerDown = useCallback(
    (e) => {
      const started = beginResize(e, containerTestId);
      if (!started) return;
      drag.current = started;
      if (collapsed) setCollapsed(false);
    },
    [collapsed, setCollapsed, containerTestId],
  );
  const onPointerMove = useCallback(
    (e) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId || !d.height) return;
      setHeight(clamp(draggedPct(d, e.clientY, dock)));
    },
    [setHeight, clamp, dock],
  );
  const onResizeEnd = useCallback((e) => {
    if (drag.current?.id !== e.pointerId) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    drag.current = null;
  }, []);
  return { onPointerDown, onPointerMove, onPointerUp: onResizeEnd, onPointerCancel: onResizeEnd };
}
